import { AssessmentOutlined, Campaign, ChecklistRtl, ContactPage, ExpandLess, ExpandMore, FactCheck, GroupAdd, HealthAndSafety, Home, ReceiptLong, RequestQuote, VerifiedUser } from '@mui/icons-material';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import MenuIcon from '@mui/icons-material/Menu';
import { Collapse, Divider, IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useAtom } from 'jotai';
import * as React from 'react';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { permissionsAtom } from '../../atoms/atom';

type MenuItemData = {
  module: string;
  path: string;
  label: string;
  icon: ReactNode;
  subItems?: MenuItemData[];
};

export default function TemporaryDrawer() {
	const [state, setState] = useState(false);
	const [permissions] = useAtom(permissionsAtom);
	const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
	const navigate = useNavigate();
	const location = useLocation();

	const toggleDrawer = (state: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
		if (
			event.type === 'keydown' &&
			((event as React.KeyboardEvent).key === 'Tab' ||
				(event as React.KeyboardEvent).key === 'Shift')
		) {
			return;
		}
		setState(state);
	};

	const modules: MenuItemData[] = useMemo(() => [
		{
			module: "INICIO",
			path: "/inicio",
			label: "Início",
			icon: <Home/>,
		},
		{
			module: "EXECUTIONS",
			path: "/execucoes",
			label: "Execuções",
			icon: <AssessmentOutlined/>,
		},
		{
			module: "EVENTS",
			path: "/execucoes-eventos",
			label: "Execuções de eventos",
			icon: <ContactPage/>,
		},
		{
			module: "FORMS",
			path: "/formularios",
			label: "Formulários",
			icon: <ChecklistRtl/>,
		},
		{
			module: "null",
			path: "/documentacoes",
			label: "Documentação",
			icon: <FindInPageIcon/>,
		},
		{
			module: "EMPLOYEES",
			path: "/cadastro-funcionarios",
			label: "Cadastro de funcionários",
			icon: <GroupAdd/>,
		},
		{
			module: "DOCUMENTS",
			path: "/envio-de-documentos",
			label: "Envio de documentos",
			icon: <ReceiptLong/>,
		},
		{
			module: "COMPLAINTS",
			path: !permissions.includes('MANAGEMENT_COMPLAINTS')
				? "/gestao-de-denuncias/painel"
				: "/gestao-de-denuncias",
			label: "Gestão de denúncias",
			icon: <Campaign/>
		},
		{
			module: "AUDIT",
			path: "/auditoria",
			label: "Auditoria de admissões",
			icon: <VerifiedUser/>,
		},
		{
			module: "EPI_INSPECTION",
			path: "/inspecao-epis",
			label: "Inspeção de EPIs",
			icon: <HealthAndSafety/>,
			subItems: [
				{
					module: "EPI_INSPECTION",
					path: "/inspecao-epis",
					label: "Painel Conformidade NR-06",
					icon: <></>,
				},
				{
					module: "CREATE_EPI_INSPECTION",
					path: "/inspecao-epis/nova",
					label: "Nova inspeção",
					icon: <></>,
				},
				{
					module: "EPI_COMPLIANCE",
					path: "/inspecao-epis/conformidade",
					label: "Gestão da Conformidade",
					icon: <></>,
				},
				{
					module: "EPI_INSPECTION",
					path: "/inspecao-epis/inspecoes",
					label: "Acompanhamento",
					icon: <></>,
				},
				{
					module: "EPI_NONCONFORMITY",
					path: "/inspecao-epis/nao-conformidades",
					label: "Não Conformidades",
					icon: <></>,
				},
				{
					module: "EPI_DASHBOARD",
					path: "/inspecao-epis/dashboard",
					label: "Dashboard",
					icon: <></>,
				}
			]
		},
		{
			module: "RISK_ASSESSMENT",
			path: "/avaliacao-riscos",
			label: "Avaliação de riscos",
			icon: <FactCheck/>,
		},
		{
			module: "FINANCIAL",
			path: "/financeiro",
			label: "Financeiro",
			icon: <RequestQuote/>,
		}
	], [permissions]);

	const handleNavigate = (address: string) => {
		navigate(address);
		setState(false);
	};
	
	const isCurrentRouteInModule = (item: MenuItemData) => {
		return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
	};

	const getDefaultExpanded = (item: MenuItemData) => {
		return !!item.subItems?.length && isCurrentRouteInModule(item);
	};

	const handleMenuClick = (item: MenuItemData) => {
		setOpenMenus((prev) => ({
		...prev,
		[item.module]: !(item.module in prev ? prev[item.module] : getDefaultExpanded(item))
		}));
	};

	return (
		<Fragment key={"drawer"}>
			<div className='icons'>
				<Tooltip title="Menu" placement='right'>
					<IconButton onClick={toggleDrawer(true)}>
						<MenuIcon sx={{fontSize: "5vh"}}/>
					</IconButton>
				</Tooltip>
			</div>
			<Drawer
				anchor="left"
				open={state}
				onClose={toggleDrawer(false)}
			>
				<Box
					sx={{ width: 305, height: 900 }}
					role="presentation"
				>
					<List>
						{modules
							.filter((item) => permissions.includes(item.module) || item.module === 'null' || item.module === 'INICIO'
								|| !!item.subItems?.some((subItem) => permissions.includes(subItem.module)))
							.map((item) => {
								const hasSubItems = item.subItems && item.subItems.length > 0;
								const isExpanded = item.module in openMenus
									? openMenus[item.module]
									: getDefaultExpanded(item);
								return (
									<Fragment key={item.module}>
										<ListItem>
											<ListItemButton
												selected={!item.subItems && location.pathname === item.path}
												onClick={() => hasSubItems ? handleMenuClick(item) : handleNavigate(item.path)}
											>
												<ListItemIcon>
													{item.icon}
												</ListItemIcon>
												<ListItemText primary={item.label} />
												{hasSubItems ? (isExpanded ? <ExpandLess /> : <ExpandMore />) : null}
											</ListItemButton>
										</ListItem>
										{hasSubItems && (
											<Collapse in={isExpanded} timeout="auto" unmountOnExit>
												<List component="div" disablePadding>
													{item.subItems!
														.filter((subItem) => permissions.includes(subItem.module) || subItem.module === 'null')
														.map((subItem) => (
															<ListItemButton
																key={subItem.path}
																sx={{ pl: 1 }}
																selected={location.pathname === subItem.path}
																onClick={() => handleNavigate(subItem.path)}
															>
																<ListItemIcon>
																	{subItem.icon}
																</ListItemIcon>
																<ListItemText primary={subItem.label} />
															</ListItemButton>
														))
													}
												</List>
											</Collapse>
										)}
										<Divider />
									</Fragment>
								);
							})
						}
					</List>
				</Box>
			</Drawer>
		</Fragment>
	);
}